import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Tag, Tooltip, Typography } from 'antd';
import * as fmt from '../format';
import {
  FAMILIES, classifySku, formatsFor, isStocked,
} from './skuFormat';

const { Text } = Typography;

/** Worst first. Drives cell colour and legend order. */
const COVER = [
  { id: 'low', label: 'Below min' },
  { id: 'ok', label: 'In range' },
  { id: 'high', label: 'Over max' },
  { id: 'none', label: 'No forecast' },
];

const COVER_RANK = COVER.reduce((acc, item, i) => ({ ...acc, [item.id]: i }), {});

const FAMILY_LABEL = FAMILIES.reduce((acc, item) => ({ ...acc, [item.id]: item.label }), {
  other: 'Other',
});

const coverOf = (weeks, min, max) => {
  if (weeks == null) return 'none';
  if (min != null && weeks < min) return 'low';
  if (max != null && weeks > max) return 'high';
  return 'ok';
};

const short = (n) => (n < 10 ? n.toFixed(1) : String(Math.round(n)));

const cellValue = (weeks, asMonths) => {
  if (weeks == null) return '–';
  if (weeks >= 52) return asMonths ? '12+' : '52+';
  return short(asMonths ? weeks / fmt.WEEKS_PER_MONTH : weeks);
};

const range = (min, max, asMonths) => {
  if (min == null && max == null) return 'No target';
  const show = (n) => {
    if (n == null) return '–';
    return asMonths ? short(n / fmt.WEEKS_PER_MONTH) : String(n);
  };
  return `${show(min)}–${show(max)} ${asMonths ? 'mo' : 'wk'} target`;
};

const coverText = (weeks, asMonths) => {
  if (weeks == null) return 'No forecast';
  if (asMonths) return weeks >= 104 ? '24+ mo' : `${short(weeks / fmt.WEEKS_PER_MONTH)} mo`;
  return fmt.weeks(weeks);
};

/** One row per beer; cells keyed by formatId, worst SKU wins when a format has several. */
const buildRows = (beers) => beers.map((beer) => {
  const rules = beer.rules || {};
  const min = rules.minCoverWeeks;
  const max = rules.maxCoverWeeks;
  const cells = {};
  (beer.skuLines || []).forEach((line) => {
    if (!line || line.viaMix || !isStocked(line)) return;
    const format = classifySku(line);
    const weeks = line.weeksOnHand == null ? null : line.weeksOnHand;
    const sku = {
      sku: line.sku,
      productName: line.productName,
      weeks,
      cover: coverOf(weeks, min, max),
    };
    const cell = cells[format.formatId];
    if (!cell) {
      cells[format.formatId] = { ...format, ...sku, skus: [sku] };
      return;
    }
    cell.skus.push(sku);
    const worse = COVER_RANK[sku.cover] < COVER_RANK[cell.cover]
      || (sku.cover === cell.cover && sku.weeks != null && sku.weeks < cell.weeks);
    if (worse) Object.assign(cell, sku);
  });
  return {
    beer,
    beerName: beer.beerName,
    min,
    max,
    cells,
  };
}).filter((row) => Object.keys(row.cells).length);

const CellTip = ({ row, cell, asMonths }) => (
  <div className="forecasting-cover-tip">
    <div><strong>{row.beerName}</strong></div>
    <div>{`${cell.formatLabel} · ${range(row.min, row.max, asMonths)}`}</div>
    {cell.skus.map((sku) => (
      <div key={sku.sku} className="forecasting-cover-tip-sku">
        <span>{sku.productName && sku.productName !== sku.sku ? sku.productName : sku.sku}</span>
        <span>{coverText(sku.weeks, asMonths)}</span>
      </div>
    ))}
  </div>
);

CellTip.propTypes = {
  row: PropTypes.shape({
    beerName: PropTypes.string,
    min: PropTypes.number,
    max: PropTypes.number,
  }).isRequired,
  cell: PropTypes.shape({
    formatLabel: PropTypes.string,
    skus: PropTypes.arrayOf(PropTypes.shape({
      sku: PropTypes.string,
      productName: PropTypes.string,
      weeks: PropTypes.number,
    })),
  }).isRequired,
  asMonths: PropTypes.bool.isRequired,
};

const NextBrew = ({ beer }) => {
  const brew = beer.nextPlannedBrew;
  if (!brew) return <Text type="secondary">None</Text>;
  return (
    <Tooltip title={`${fmt.date(brew.brewDate)} · ${fmt.litres(brew.volumeLitres)} · ${brew.brewery}`}>
      <span>{fmt.shortDate(brew.brewDate)}</span>
    </Tooltip>
  );
};

NextBrew.propTypes = {
  beer: PropTypes.shape({
    nextPlannedBrew: PropTypes.shape({
      brewDate: PropTypes.string,
      volumeLitres: PropTypes.number,
      brewery: PropTypes.string,
    }),
  }).isRequired,
};

/** Cover grid for beers already filtered and sorted by the caller. */
const CoreRangeChart = ({
  beers, family, asMonths, onOpenBeer,
}) => {
  const [cover, setCover] = useState(null);
  const rows = useMemo(() => buildRows(beers), [beers]);
  const allCells = useMemo(() => rows.flatMap((row) => Object.values(row.cells)), [rows]);
  const columns = useMemo(() => formatsFor(allCells, family), [allCells, family]);
  const groups = useMemo(() => {
    const list = [];
    columns.forEach((col) => {
      const id = col.id.split(':')[0];
      const last = list[list.length - 1];
      if (last && last.id === id) last.cols.push(col);
      else list.push({ id, label: FAMILY_LABEL[id] || id, cols: [col] });
    });
    return list;
  }, [columns]);

  const inView = useMemo(() => {
    const ids = new Set(columns.map((col) => col.id));
    return rows
      .map((row) => ({
        row,
        cells: Object.values(row.cells).filter((cell) => ids.has(cell.formatId)),
      }))
      .filter((item) => item.cells.length);
  }, [rows, columns]);

  const counts = useMemo(() => {
    const tally = {};
    inView.forEach(({ cells }) => cells.forEach((cell) => {
      tally[cell.cover] = (tally[cell.cover] || 0) + 1;
    }));
    return tally;
  }, [inView]);

  const visible = cover
    ? inView.filter(({ cells }) => cells.some((cell) => cell.cover === cover)).map((i) => i.row)
    : inView.map((i) => i.row);
  const order = visible.map((row) => row.beerName);

  return (
    <div className="forecasting-cover">
      <div className="forecasting-cover-legend">
        {COVER.map((item) => (
          <button
            key={item.id}
            type="button"
            className={[
              'forecasting-cover-chip',
              `is-${item.id}`,
              cover === item.id ? 'is-active' : '',
              cover && cover !== item.id ? 'is-muted' : '',
            ].join(' ')}
            onClick={() => setCover((current) => (current === item.id ? null : item.id))}
          >
            <i />
            {item.label}
            <b>{counts[item.id] || 0}</b>
          </button>
        ))}
        <Text type="secondary" className="forecasting-cover-hint">
          {cover
            ? `Showing beers with a SKU ${COVER.find((item) => item.id === cover).label.toLowerCase()}`
            : `SKU counts. Cells show ${asMonths ? 'months' : 'weeks'} on hand.`}
        </Text>
      </div>

      {visible.length ? (
        <div className="forecasting-cover-scroll">
          <table className="forecasting-cover-grid">
            <thead>
              <tr>
                <th rowSpan={2} className="forecasting-cover-beer-col">Beer</th>
                <th rowSpan={2} className="forecasting-cover-meta">Status</th>
                {groups.map((group) => (
                  <th key={group.id} colSpan={group.cols.length} className="forecasting-cover-group">
                    {group.label}
                  </th>
                ))}
                <th rowSpan={2} className="forecasting-cover-meta is-first">Next brew</th>
              </tr>
              <tr>
                {groups.map((group) => group.cols.map((col, i) => (
                  <th
                    key={col.id}
                    title={col.label}
                    className={i === 0 ? 'forecasting-cover-format is-first' : 'forecasting-cover-format'}
                  >
                    {col.label.replace(/ keg$/, '')}
                  </th>
                )))}
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => {
                const status = fmt.STATUS[row.beer.status];
                return (
                  <tr key={row.beerName} onClick={() => onOpenBeer(row.beerName, order)}>
                    <th scope="row" className="forecasting-cover-beer-col">
                      <div className="forecasting-cover-beer">
                        {row.beer.tapBadgeUrl
                          ? <img src={row.beer.tapBadgeUrl} alt="" />
                          : <span className="forecasting-cover-badge-empty" />}
                        <div>
                          <div className="forecasting-cover-name">{row.beerName}</div>
                          <div className="forecasting-cover-target">
                            {range(row.min, row.max, asMonths)}
                          </div>
                        </div>
                      </div>
                    </th>
                    <td className="forecasting-cover-meta">
                      {status && <Tag color={status.color}>{status.label}</Tag>}
                    </td>
                    {groups.map((group) => group.cols.map((col, i) => {
                      const cell = row.cells[col.id];
                      const first = i === 0 ? ' is-first' : '';
                      if (!cell) return <td key={col.id} className={`forecasting-cover-cell${first}`} />;
                      const muted = cover && cell.cover !== cover ? ' is-muted' : '';
                      return (
                        <td key={col.id} className={`forecasting-cover-cell${first}`}>
                          <Tooltip
                            title={<CellTip row={row} cell={cell} asMonths={asMonths} />}
                            mouseEnterDelay={0.15}
                          >
                            <span className={`forecasting-cover-value is-${cell.cover}${muted}`}>
                              {cellValue(cell.weeks, asMonths)}
                              {cell.skus.length > 1 && <sup>{cell.skus.length}</sup>}
                            </span>
                          </Tooltip>
                        </td>
                      );
                    }))}
                    <td className="forecasting-cover-meta is-first">
                      <NextBrew beer={row.beer} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="forecasting-cover-empty">
          <Text type="secondary">No stocked SKUs match these filters</Text>
        </div>
      )}
    </div>
  );
};

CoreRangeChart.propTypes = {
  beers: PropTypes.arrayOf(PropTypes.shape({
    beerName: PropTypes.string,
    tapBadgeUrl: PropTypes.string,
    status: PropTypes.string,
    nextPlannedBrew: PropTypes.shape({
      brewDate: PropTypes.string,
      volumeLitres: PropTypes.number,
      brewery: PropTypes.string,
    }),
    rules: PropTypes.shape({
      minCoverWeeks: PropTypes.number,
      maxCoverWeeks: PropTypes.number,
    }),
    skuLines: PropTypes.arrayOf(PropTypes.shape({
      sku: PropTypes.string,
      productName: PropTypes.string,
      viaMix: PropTypes.string,
      litresPerUnit: PropTypes.number,
      weeksOnHand: PropTypes.number,
      stockLitres: PropTypes.number,
      stockUnits: PropTypes.number,
      stockUnitsByBucket: PropTypes.objectOf(PropTypes.number),
    })),
  })).isRequired,
  family: PropTypes.string,
  asMonths: PropTypes.bool,
  onOpenBeer: PropTypes.func.isRequired,
};

CoreRangeChart.defaultProps = {
  family: 'all',
  asMonths: false,
};

export default CoreRangeChart;
